import React, { useEffect, useState } from "react";
import "./Reminder.scss";
import Toggle from "react-toggle";
import { Plus, Minus } from "lucide-react";
import { NONE_MENU_ID } from "../../../common/constants";
import { setMenu } from "../../../redux/reducers/popupSlice";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  addUpdateReminder,
  disableDocumentReminder,
  updateReminderDateForDocument,
} from "../../../redux/reducers/documentsDataSlice";
import { selectSelectedDocumentId } from "../../../redux/reducers/dashboardSlice";
import {
  selectDocumentReminder,
  selectDocumentReminderId,
  setReminder,
  setReminderId,
} from "../../../redux/reducers/documentSlice";
import { selectUserReminderPeriod } from "../../../redux/reducers/settingsSlice";

// ---------- Helpers (date-only, local-midnight) ----------

const pad2 = (n: number) => (n < 10 ? `0${n}` : `${n}`);

function localMidnightFromDate(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

function parseStoredDate(raw: string | null | undefined): Date | null {
  if (!raw) return null;
  // accept 'YYYY-MM-DD', 'YYYY-MM-DDT00:00:00', 'YYYY-MM-DD 00:00:00'
  const d = new Date(raw); // UTC instant
  if (isNaN(d.getTime())) return null;

  // Extract the *local* calendar date parts
  // const y = d.getFullYear();
  // const m = d.getMonth();
  // const day = d.getDate();

  // // Construct a new date at local midnight
  // return new Date(y, m, day, d.getHours(), d.getMinutes(), d.getSeconds(), 0);
  return d;
}

function formatStoredDate(d: Date) {
  // format to 'YYYY-MM-DD' (same as .toISOString().split('T')[0] but safe)
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T00:00:00`;
}

// function formatStoredDateToUTC(d: Date) {
//   return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}T${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}:${pad2(d.getUTCSeconds())}+00:00`;
// }

function isWeekendLocal(d: Date) {
  const day = d.getDay();
  return day === 6 || day === 0; // Saturday (6) or Sunday (0) only
}

function addDaysLocal(d: Date, delta: number) {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  out.setDate(out.getDate() + delta);
  return out;
}

function addMonthsLocal(d: Date, delta: number) {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  out.setMonth(out.getMonth() + delta);
  return localMidnightFromDate(out);
}

function addYearsLocal(d: Date, delta: number) {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  out.setFullYear(out.getFullYear() + delta);
  return localMidnightFromDate(out);
}

// Return next working day given reminderPeriod (count working days; if today is weekend start from next Monday)
function getNextWorkDay(reminderPeriod: number): Date {
  const today = localMidnightFromDate(new Date());
  let cur = new Date(today);

  // if today is weekend, move to next Monday first
  while (isWeekendLocal(cur)) {
    cur = addDaysLocal(cur, 1);
  }

  let added = 0;
  while (added < reminderPeriod) {
    cur = addDaysLocal(cur, 1);
    if (!isWeekendLocal(cur)) added++;
  }
  return cur;
}

// ---------- Component ----------

type Props = {};

export default function ReminderMenu(_: Props) {
  const dispatch = useAppDispatch();
  const reminderDateRaw = useAppSelector(selectDocumentReminder); // maybe null or 'YYYY-MM-DD' or other
  const reminderId = useAppSelector(selectDocumentReminderId);
  const selectedDoc = useAppSelector(selectSelectedDocumentId);
  const userReminderPeriod = Number(
    useAppSelector(selectUserReminderPeriod).value || 2
  );

  // initialize selectedDate from stored reminderDate or default nextWorkDay
  const initialFromStore =
    parseStoredDate(reminderDateRaw) ?? getNextWorkDay(userReminderPeriod);

  const [isReminderActive, setReminderActive] = useState<boolean>(true);
  const [selectedDate, setSelectedDate] = useState<Date>(
    localMidnightFromDate(initialFromStore)
  );
  const [isActive, setActive] = useState<number>(0); // 0 day,1 month,2 date,3 year

  const [initialState, setInitialState] = useState<{
    isReminderActive: boolean;
    selectedDate: Date;
  }>({
    isReminderActive: true,
    selectedDate: localMidnightFromDate(initialFromStore),
  });

  const [changeOccured, setChangeOccured] = useState(false);

  useEffect(() => {
    const initialDate =
      parseStoredDate(reminderDateRaw) ?? getNextWorkDay(userReminderPeriod);
    setReminderActive(true);
    setSelectedDate(localMidnightFromDate(initialDate));
    setInitialState({
      isReminderActive: true,
      selectedDate: localMidnightFromDate(initialDate),
    });
    setChangeOccured(!reminderDateRaw);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminderId]);

  const stopPropogation = (e: any) => e.stopPropagation();
  const handleClose = () => dispatch(setMenu(NONE_MENU_ID));

  const handleCancel = () => {
    setReminderActive(initialState.isReminderActive);
    setSelectedDate(localMidnightFromDate(initialState.selectedDate));
    setActive(0);
    dispatch(setMenu(NONE_MENU_ID));
  };

  const saveUpdateHandle = () => {
    if (isReminderActive) {
      const dateStrUTC = formatStoredDate(selectedDate);
      dispatch(
        addUpdateReminder({
          docId: selectedDoc,
          timeZone: "Etc/UTC",
          dateTime: dateStrUTC,
        })
      ).then((e: any) => {
        if (e.payload?.data) {
          dispatch(
            updateReminderDateForDocument({
              documentId: selectedDoc,
              reminderDate: dateStrUTC,
            })
          );
        }
      });
      dispatch(setReminderId(""));
      dispatch(setReminder(false));
    } else {
      dispatch(disableDocumentReminder({ docId: selectedDoc }));
    }
    dispatch(setMenu(NONE_MENU_ID));
    setInitialState({
      isReminderActive,
      selectedDate: localMidnightFromDate(selectedDate),
    });
    setChangeOccured(false);
  };

  // ---------- Month/Year support: these run when isActive === 1 or 3 ----------
  // We'll handle them in the same incDecHandler for simplicity above by checking isActive.
  // But users may want month/year increments: update incDecHandler to handle those too.

  // We'll implement augmented inc/dec that also respects month/year if isActive != 0/2:
  const incDecHandlerWithMonthYear = (type: "increment" | "decrement") => {
    if (!isReminderActive) return;

    setSelectedDate((prev) => {
      const today = localMidnightFromDate(new Date());
      let current = localMidnightFromDate(prev);

      // Helper: step one calendar day then skip Sat/Sun only
      const stepOneWorkingDay = (base: Date, dir: 1 | -1) => {
        const candidateBeforeWeekend = addDaysLocal(base, dir);
        let candidate = localMidnightFromDate(candidateBeforeWeekend);
        let safety = 0;
        while (isWeekendLocal(candidate) && safety < 7) {
          candidate = addDaysLocal(candidate, dir);
          safety++;
        }
        return {
          candidateBeforeWeekend,
          final: localMidnightFromDate(candidate),
        };
      };

      // If incrementing and stored date is in the past -> reset to today (or next weekday) and RETURN
      if (type === "increment" && current.getTime() < today.getTime()) {
        let base = localMidnightFromDate(today);
        // if today is Sat/Sun, advance to Monday
        while (isWeekendLocal(base)) base = addDaysLocal(base, 1);
        return base;
      }

      // DAY / DATE level
      if (isActive === 0 || isActive === 2) {
        if (type === "increment") {
          // normal forward step (current >= today)
          const { candidateBeforeWeekend, final } = stepOneWorkingDay(
            current,
            1
          );
          const blocked = final.getTime() < today.getTime();
          if (blocked) return current;
          return final;
        } else {
          // decrement
          if (current.getTime() <= today.getTime()) {
            return current;
          }
          const { candidateBeforeWeekend, final } = stepOneWorkingDay(
            current,
            -1
          );
          if (final.getTime() < today.getTime()) {
            return current;
          }
          return final;
        }
      }

      // MONTH level
      if (isActive === 1) {
        if (type === "increment") {
          const candBefore = addMonthsLocal(current, 1);
          let cand = localMidnightFromDate(candBefore);
          let safety = 0;
          while (isWeekendLocal(cand) && safety < 7) {
            cand = addDaysLocal(cand, 1);
            safety++;
          }
          if (cand.getTime() < today.getTime()) {
            return localMidnightFromDate(today);
          }
          return localMidnightFromDate(cand);
        } else {
          const candTest = addMonthsLocal(current, -1);
          if (candTest.getTime() < today.getTime()) {
            return current;
          }
          let cand = localMidnightFromDate(candTest);
          let safety = 0;
          while (isWeekendLocal(cand) && safety < 7) {
            cand = addDaysLocal(cand, -1);
            safety++;
          }
          return localMidnightFromDate(cand);
        }
      }

      // YEAR level
      if (isActive === 3) {
        if (type === "increment") {
          const candBefore = addYearsLocal(current, 1);
          let cand = localMidnightFromDate(candBefore);
          let safety = 0;
          while (isWeekendLocal(cand) && safety < 7) {
            cand = addDaysLocal(cand, 1);
            safety++;
          }
          if (cand.getTime() < today.getTime()) {
            return localMidnightFromDate(today);
          }
          return localMidnightFromDate(cand);
        } else {
          const candTest = addYearsLocal(current, -1);
          if (candTest.getTime() < today.getTime()) {
            return current;
          }
          let cand = localMidnightFromDate(candTest);
          let safety = 0;
          while (isWeekendLocal(cand) && safety < 7) {
            cand = addDaysLocal(cand, -1);
            safety++;
          }
          return localMidnightFromDate(cand);
        }
      }

      // fallback
      return current;
    });
  };

  // We'll wire the actions to use the month/year aware handler
  const incDec = (type: "increment" | "decrement") => {
    incDecHandlerWithMonthYear(type);
  };

  const toggleReminder = () => {
    if (isReminderActive) {
      setReminderActive(false);
      setActive(1);
      setSelectedDate(getNextWorkDay(userReminderPeriod));
    } else {
      setReminderActive(true);
      setActive(0);
    }
  };

  // Compare current state vs initial snapshot to show Save button
  const isDateEqual = (d1: Date, d2: Date) =>
    d1.toDateString() === d2.toDateString();

  useEffect(() => {
    const changed =
      reminderDateRaw === null ||
      isReminderActive !== initialState.isReminderActive ||
      !isDateEqual(selectedDate, initialState.selectedDate);
    setChangeOccured(changed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReminderActive, selectedDate, initialState]);

  // canDecrement: compute whether clicking minus would move before today
  const canDecrement = (() => {
    if (!isReminderActive) return false;
    const today = localMidnightFromDate(new Date());
    const current = localMidnightFromDate(selectedDate);

    // if current is already <= today, cannot decrement
    if (current.getTime() <= today.getTime()) return false;

    // simulate one step back depending on active
    if (isActive === 0 || isActive === 2) {
      let cand = addDaysLocal(current, -1);
      // skip weekends backward
      let safety = 0;
      while (isWeekendLocal(cand) && safety < 7) {
        cand = addDaysLocal(cand, -1);
        safety++;
      }
      return cand.getTime() >= today.getTime();
    } else if (isActive === 1) {
      const cand = addMonthsLocal(current, -1);
      if (cand.getTime() < today.getTime()) return false;
      let tmp = cand;
      let safety = 0;
      while (isWeekendLocal(tmp) && safety < 7) {
        tmp = addDaysLocal(tmp, -1);
        safety++;
      }
      return tmp.getTime() >= today.getTime();
    } else if (isActive === 3) {
      const cand = addYearsLocal(current, -1);
      if (cand.getTime() < today.getTime()) return false;
      let tmp = cand;
      let safety = 0;
      while (isWeekendLocal(tmp) && safety < 7) {
        tmp = addDaysLocal(tmp, -1);
        safety++;
      }
      return tmp.getTime() >= today.getTime();
    }
    return false;
  })();

  const disabilityCheck = isReminderActive
    ? "date-element-heading"
    : "date-element-heading disable";

  // UI strings
  const weekdayShort = selectedDate.toLocaleDateString("en-US", {
    weekday: "short",
  });
  const monthShort = selectedDate.toLocaleDateString("en-US", {
    month: "short",
  });

  return (
    <div onClick={stopPropogation}>
      <div className="reminder-container">
        <div className="reminder-top">
          <div>
            <p className="heading-text">Reminder</p>
          </div>
          <div>
            <Toggle
              icons={false}
              checked={isReminderActive}
              onChange={toggleReminder}
            />
          </div>
        </div>

        <div className="reminder-center">
          <hr className="horizontalLine" />
        </div>

        <div className="reminder-bottom">
          <div className="reminder-bottom-date-picker">
            <div className="date-element">
              <p className={disabilityCheck}>Day</p>
              <p
                className={`${
                  isReminderActive
                    ? isActive === 0
                      ? "active date-element-value"
                      : " date-element-value"
                    : "date-element-value disable"
                }`}
                onClick={() => isReminderActive && setActive(0)}
              >
                {weekdayShort}
              </p>
            </div>

            <div className="date-element">
              <p className={disabilityCheck}>Month</p>
              <p
                className={`${
                  isReminderActive
                    ? isActive === 1
                      ? "active date-element-value"
                      : " date-element-value"
                    : "date-element-value disable"
                }`}
                onClick={() => isReminderActive && setActive(1)}
              >
                {monthShort}
              </p>
            </div>

            <div className="date-element">
              <p className={disabilityCheck}>Date</p>
              <p
                className={`${
                  isReminderActive
                    ? isActive === 2
                      ? "active date-element-value"
                      : " date-element-value"
                    : "date-element-value disable"
                }`}
                onClick={() => isReminderActive && setActive(2)}
              >
                {selectedDate.getDate()}
              </p>
            </div>

            <div className="date-element">
              <p className={disabilityCheck}>Year</p>
              <p
                className={`${
                  isReminderActive
                    ? isActive === 3
                      ? "active date-element-value"
                      : " date-element-value"
                    : "date-element-value disable"
                }`}
                onClick={() => isReminderActive && setActive(3)}
              >
                {selectedDate.getFullYear()}
              </p>
            </div>
          </div>

          <div className="reminder-bottom-date-actions">
            <Minus
              size={28}
              color={`${
                isReminderActive && canDecrement ? "#FFB000" : "#585858"
              }`}
              strokeWidth={2.5}
              style={{
                cursor: `${
                  isReminderActive && canDecrement ? "pointer" : "default"
                }`,
              }}
              onClick={() =>
                isReminderActive && canDecrement && incDec("decrement")
              }
            />
            <Plus
              size={28}
              color={`${isReminderActive ? "#FFB000" : "#585858"}`}
              strokeWidth={2.5}
              style={{ cursor: `${isReminderActive ? "pointer" : "default"}` }}
              onClick={() => isReminderActive && incDec("increment")}
            />
          </div>
        </div>

        <div className="reminder-button-container">
          {changeOccured && !(!isReminderActive && reminderDateRaw === null) ? (
            <>
              <button className="reminder-close-button" onClick={handleCancel}>
                Cancel
              </button>
              <button
                className="reminder-close-button"
                onClick={saveUpdateHandle}
              >
                Save
              </button>
            </>
          ) : (
            <button className="reminder-close-button" onClick={handleClose}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
